import { useTranslations } from '@vocab/react';
import {
  Box,
  IconHelp,
  Text,
  Toggle,
  TooltipRenderer,
} from 'braid-design-system';
import type React from 'react';
import type { ReactNode } from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import { useFeatureFlag } from '../FeatureFlag/FeatureFlag';

import type { ToggleEssentialQuestionHandler } from './Question';

interface Props {
  id: string;
  essential?: boolean | null;
  onChange: ToggleEssentialQuestionHandler;
}

interface ConditionalProps {
  on: boolean;
  children?: React.ReactNode;
}

const ConditionalStrong: React.FC<ConditionalProps> = ({ on, children }) =>
  on ? <strong>{children}</strong> : <>{children}</>;

export const EssentialToggle: React.FC<Props> = ({
  id,
  essential,
  onChange,
}) => {
  const { t } = useTranslations(translations);
  const showToolTip = useFeatureFlag('mustHaveToolTip');
  return (
    <Box paddingTop="xxsmall" display="flex" justifyContent="flexStart">
      <Box paddingRight="xsmall">
        <Toggle
          id={`essential-question-toggle-${id}`}
          label={t('mustHave.toggle', {
            ConditionalStrong: (children: ReactNode) => (
              <ConditionalStrong on={!essential}>{children}</ConditionalStrong>
            ),
          })}
          on={essential === true}
          onChange={() => onChange(id)}
          size="small"
          bleedY
        />
      </Box>

      {showToolTip && (
        <TooltipRenderer
          id={`essential-question-tooltip-${id}`}
          tooltip={<Text>{t('mustHave.tooltip')}</Text>}
        >
          {({ triggerProps }) => (
            <Box aria-label="Help" {...triggerProps}>
              <Text size="small">
                <IconHelp />
              </Text>
            </Box>
          )}
        </TooltipRenderer>
      )}
    </Box>
  );
};
